import { ChangeDetectionStrategy, Component, EventEmitter, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';

import { BaseEntityModel } from '../../../core/abstractions/base-entity.model';
import { BaseEntityResource } from '../../../core/abstractions/base-entity.resource';
import { BaseComponent } from '../../../core/abstractions/base.component';
import { ButtonMapper } from '../../../core/utils/mappers/button.mapper';
import { Tools } from '../../../core/utils/tools';
import { ActionButtonModel } from '../../../models/core/action-button.model';
import { ApiResponseModel } from '../../../models/core/api-response.model';
import { ActionButtonsService } from '../../../services/action-buttons.service';
import { ControllerService } from '../../../services/controller.service';
import { ErrorsService } from '../../../services/errors.service';
import { GridStatusService } from '../../../services/grid-status.service';
import { NotificationService } from '../../../services/notification.service';

import Utils = Tools.Utils;

@Component({
    selector: 'sl-modal-form',
    templateUrl: './modal-form.component.html',
    styleUrls: ['./modal-form.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ModalFormComponent<
    T extends BaseEntityModel,
    TSummary extends BaseEntityModel = T,
    TListEntityModel extends BaseEntityModel = T
>
    extends BaseComponent
    implements OnInit, OnDestroy
{
    @Input() entityResource: BaseEntityResource<T, TSummary, TListEntityModel>;
    @Input() formGroup: FormGroup;
    @Input() isLoading = true;
    @Input() title: string;

    record?: T;
    recordSummary?: TSummary;
    recordId?: string;
    isEdit = false;
    isView = false;
    shouldLoadSummary = true;
    loadingStatusChange = new Subject<boolean>();
    currentButtons: ActionButtonModel[] = [];

    private baseButtons: ActionButtonModel[] = [];
    private errorSubscription?: Subscription;
    private readonly dialogRef = inject(MatDialogRef<ModalFormComponent<T, TSummary, TListEntityModel>>);
    private readonly controllerService = inject(ControllerService);
    private readonly actionButtonsService = inject(ActionButtonsService);
    private readonly notificationService = inject(NotificationService);
    private readonly errorsService = inject(ErrorsService);
    private readonly gridStatusService = inject(GridStatusService);

    ngOnInit(): void {
        this.changeLoadingStatus(true);
        this.setupInitialData();

        const cancelButton = ButtonMapper.cancel;

        if (!cancelButton.iconClasses()) {
            cancelButton.iconClasses.set([]);
        }

        cancelButton.iconClasses.update(v => [...v, 'cancel']);
        this.baseButtons.push(cancelButton.withAction(() => this.dialogRef.close()));

        const saveButton = ButtonMapper.save;

        if (!saveButton.iconClasses()) {
            saveButton.iconClasses.set([]);
        }

        saveButton.iconClasses.update(v => [...v, 'save']);

        this.baseButtons.push(saveButton.withAction(() => this.save()));
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();

        if (!!this.errorSubscription) {
            this.errorSubscription.unsubscribe();
        }
    }

    save(): void {
        if (this.isView) {
            return;
        }

        this.record = this.record || ({} as T);
        Object.assign(this.record, this.formGroup.value);

        if (this.formGroup.dirty && this.formGroup.valid) {
            if (this.isEdit) {
                this.entityResource.edit(this.record).subscribe(r => {
                    if (!!r.value) {
                        this.notificationService.showSuccess('General.SuccessfullySaved');

                        this.gridStatusService.reloadGrid();
                        this.dialogRef.close();
                    }
                });
            } else {
                this.entityResource.add(this.record).subscribe(r => {
                    if (!!r.value) {
                        this.notificationService.showSuccess('General.SuccessfullySaved');
                        this.recordId = r.value.result.toString();

                        this.gridStatusService.reloadGrid();
                        this.dialogRef.close();
                    }
                });
            }
        } else {
            Object.keys(this.formGroup.controls).forEach(key => {
                const control = this.formGroup.controls[key];

                control.markAsDirty();
                control.markAsTouched();
                control.updateValueAndValidity();

                (control.statusChanges as EventEmitter<any>).emit(control.status);
            });
        }
    }

    onButtonClick(button: ActionButtonModel, event: MouseEvent): void {
        if (!!button.clickFunction) {
            button.clickFunction(event);
        }
    }

    private setupInitialData(): void {
        const currentResponse = this.controllerService.currentResponse;

        if (!this.isView) {
            this.errorSubscription = this.errorsService.onBusinessException.subscribe(r =>
                Utils.displayBusinessExceptions(this.formGroup, r)
            );
        } else if (!!this.errorSubscription) {
            this.errorSubscription.unsubscribe();
        }

        if (!!currentResponse) {
            // setTimeout is required because the content from ControllerService.execute is loaded before
            // the view. Because of that, the data will change and angular will throw an exception in dev.
            // To prevent that, we use setTimeout.
            setTimeout(() => this.loadRecord(currentResponse), 0);
        } else {
            if (this.isEdit) {
                this.entityResource.get(this.recordId!).subscribe({
                    next: r => this.loadRecord(r),
                    error: () => this.changeLoadingStatus(false)
                });
            } else if (this.isView) {
                if (this.shouldLoadSummary) {
                    this.entityResource.getSummary(this.recordId!).subscribe({
                        next: r => this.loadRecord(r),
                        error: () => this.changeLoadingStatus(false)
                    });
                } else {
                    this.entityResource.get(this.recordId!).subscribe({
                        next: r => this.loadRecord(r),
                        error: () => this.changeLoadingStatus(false)
                    });
                }
            } else {
                this.currentButtons = this.baseButtons;
                this.changeLoadingStatus(false);
            }
        }
    }

    private loadRecord(response: ApiResponseModel<any>): void {
        this.record = response.value as T;
        this.recordSummary = response.value as TSummary;
        this.title = response.contentTitle;

        if (this.isView) {
            this.currentButtons = this.actionButtonsService.convert(response.relatedActions);
        } else {
            this.currentButtons = this.baseButtons;
        }

        this.resetFormGroup();
        this.changeLoadingStatus(false);

        this.controllerService.currentResponse = undefined;
    }

    private changeLoadingStatus(isLoading: boolean): void {
        this.isLoading = isLoading;
        this.loadingStatusChange.next(isLoading);
    }

    private resetFormGroup(): void {
        if (!!this.formGroup && !!this.record) {
            this.formGroup.patchValue(this.record);
            this.formGroup.markAsPristine();
        }
    }
}
